<template>
  <main class="yay-recharge-page">
    <div class="recharge-shell">
      <button class="brand" type="button" @click="goHome">
        <img src="@/img/logo.svg" alt="" class="brand-icon">
        <span>Yaychat</span>
      </button>

      <section class="account-section">
        <div class="gift-banner">
          <div class="gift-copy">
            <h1>{{ $t('recharge.firstTopUpGift') }}</h1>
            <p>{{ $t('recharge.firstTopUpGiftDescription') }}</p>
          </div>
          <img src="@/img/recharge/recharge_gift.webp" alt="" class="gift-image">
        </div>

        <div class="account-card" :class="{ 'has-user': user }">
          <div class="input-wrap" :class="{ active: user }">
            <input
              v-model="user_id"
              class="user-input"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              :placeholder="$t('recharge.enterUserIdToTopUp')"
              @input="checkUid"
              @blur="saveUid"
              @keyup.enter="$event.target.blur()"
            >
            <button
              v-if="user_id"
              class="clear-button"
              type="button"
              :aria-label="$t('recharge.clearUserId')"
              @mousedown.prevent
              @click="clearUserInput"
            >
              <span aria-hidden="true"></span>
            </button>
          </div>

          <div v-if="user" class="user-info">
            <img :src="user.avatar" alt="" class="user-avatar">
            <strong class="user-name">{{ user.nickName }}</strong>
            <span v-if="isNewUser" class="first-top-up">{{ $t('recharge.firstTopUp') }}</span>
          </div>
        </div>
      </section>

      <section class="amount-section">
        <h2>{{ $t('recharge.selectTopUpAmount') }}</h2>
        <div v-if="productsLoading" class="coin-options">
          <span v-for="index in 6" :key="index" class="coin-skeleton skeleton-shimmer"></span>
        </div>
        <div v-else class="coin-options">
          <button
            v-for="item in recharge_list"
            :key="item.id"
            type="button"
            class="coin-option"
            :class="{ active: item.id === select_recharge_id }"
            @click="priceChangeHandle(item)"
          >
            <span
              class="coin-amount"
              :class="{ compact: String(item.coin).length >= 6 }"
            >
              <img src="@/img/recharge/oumi_coin.webp" alt="" class="coin-icon">
              <strong>{{ item.coin }}</strong>
            </span>
            <span class="coin-label">${{ (Number(item.coin) / 50).toFixed(2) }}</span>
          </button>
        </div>
      </section>

      <section class="payment-section">
        <template v-if="productsLoading">
          <span v-for="index in 3" :key="index" class="payment-skeleton skeleton-shimmer"></span>
        </template>
        <template v-for="payType in visiblePayTypes" v-else :key="payType">
          <button
            v-if="payType === 'STRIPE'"
            class="payment-button stripe-payment"
            type="button"
            :disabled="!user || clickPayItem"
            @click="stripePayClick"
          >
            <span class="payment-content">
              <span class="payment-icon-wrap">
                <span v-if="clickPayItem === 'STRIPE'" class="loading-spinner"></span>
                <img v-else src="@/img/ic_stripe.svg" alt="" class="payment-icon">
              </span>
              <strong>{{ clickPayItem === 'STRIPE' ? $t('recharge.paying') : 'StripePay' }}</strong>
              <b>{{ paymentPrice('STRIPE') }}</b>
              <del>{{ originalPrice }}</del>
            </span>
            <span class="discount-flag small">
              <span>{{ $t('recharge.discount375Off') }}</span>
            </span>
          </button>

          <button
            v-else-if="payType === 'PAYERMAX'"
            class="payment-button payermax-payment"
            type="button"
            :disabled="!user || clickPayItem"
            @click="payermaxClick"
          >
            <span class="payment-content">
              <span class="payment-icon-wrap">
                <span v-if="clickPayItem === 'PAYERMAX'" class="loading-spinner"></span>
                <img v-else src="@/img/ic_payermax.svg" alt="" class="payment-icon">
              </span>
              <strong>{{ clickPayItem === 'PAYERMAX' ? $t('recharge.paying') : $t('recharge.payermax') }}</strong>
              <b>{{ paymentPrice('PAYERMAX') }}</b>
              <del>{{ originalPrice }}</del>
            </span>
            <span class="discount-flag large">
              <span>{{ $t('recharge.discount375Off') }}</span>
            </span>
          </button>

          <button
            v-else-if="payType === 'PAYPAL'"
            class="payment-button paypal-payment"
            type="button"
            :disabled="!user || clickPayItem"
            @click="customPaypalClick"
          >
            <span class="payment-content">
              <span class="payment-icon-wrap">
                <span v-if="clickPayItem === 'PAYPAL'" class="loading-spinner"></span>
                <img v-else src="@/img/ic_paypal.svg" alt="" class="payment-icon">
              </span>
              <strong>{{ clickPayItem === 'PAYPAL' ? $t('recharge.paying') : $t('recharge.paypal') }}</strong>
              <b>{{ paymentPrice('PAYPAL') }}</b>
              <del>{{ originalPrice }}</del>
            </span>
            <span class="discount-flag small">
              <span>{{ $t('recharge.discount285Off') }}</span>
            </span>
          </button>
        </template>
      </section>
    </div>

    <div v-if="loading" class="loading-overlay">
      <span class="page-loader"></span>
      <span>{{ $t('recharge.coinsAreBeingDistributed') }}</span>
    </div>
  </main>
</template>

<script setup>
import router from '#/router'
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useOumiRecharge } from '../composables/useOumiRecharge'

const {
  loading,
  productsLoading,
  select_recharge_id,
  select_recharge_item,
  paypal_product_item,
  recharge_list,
  supportedPayTypes,
  user,
  user_id,
  isNewUser,
  clickPayItem,
  isPaymentMethodAvailable,
  priceChangeHandle,
  checkUid,
  saveUid,
  clearUserInput,
  stripePayClick,
  payermaxClick,
  customPaypalClick,
} = useOumiRecharge()

const paymentOrder = ['STRIPE', 'PAYERMAX', 'PAYPAL']

const visiblePayTypes = computed(() => {
  const normalized = supportedPayTypes.value.map(type => (
    type === 'DIANDIAN' ? 'STRIPE' : type
  ))

  return paymentOrder.filter(type => (
    normalized.includes(type) && isPaymentMethodAvailable(type)
  ))
})

const originalPrice = computed(() => {
  if (!select_recharge_item.value) return ''

  const amount = Number(select_recharge_item.value.coin) / 50
  return `($${amount.toFixed(2)})`
})

function paymentPrice(payType) {
  if (payType === 'PAYPAL') {
    return paypal_product_item.value?.localPrice || '$0.00'
  }

  return select_recharge_item.value?.localPrice
    || `$${select_recharge_item.value?.cent || '0.00'}`
}

function goHome() {
  router.push('/')
}

onMounted(() => {
  document.documentElement.classList.add('yay-recharge-mobile-root')
  document.body.classList.add('yay-recharge-mobile-root')
})

onBeforeUnmount(() => {
  document.documentElement.classList.remove('yay-recharge-mobile-root')
  document.body.classList.remove('yay-recharge-mobile-root')
})
</script>

<style lang="scss" scoped>
:global(html.yay-recharge-mobile-root),
:global(body.yay-recharge-mobile-root) {
  min-height: 100%;
  background: #dddfff;
}

:global(body.yay-recharge-mobile-root) {
  overflow-x: hidden;
}

.yay-recharge-page {
  width: 100%;
  min-height: 100vh;
  padding: 12px 16px 38px;
  box-sizing: border-box;
  overflow-x: hidden;
  color: #333333;
  background:
    linear-gradient(rgba(221, 223, 255, 0.53), rgba(221, 223, 255, 0.53)),
    url('@/img/recharge/yaychat_recharge_bg.webp') top center / 100% auto no-repeat,
    #dddfff;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Poppins', sans-serif;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.recharge-shell {
  width: 100%;
  max-width: 568px;
  margin: 0 auto;
}

.brand {
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  color: #111111;
  background: transparent;
  font: 900 16px/1 'Poppins', sans-serif;
  cursor: pointer;

  .brand-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }
}

.account-section {
  margin-top: 17px;
}

.gift-banner {
  position: relative;
  height: 129px;
  border-radius: 16px;
  background: linear-gradient(103deg, #0719ff 2%, #bb00ff 91%);
}

.gift-copy {
  position: relative;
  z-index: 2;
  width: min(58%, 220px);
  padding: 12px 16px;
  box-sizing: border-box;

  h1,
  p {
    margin: 0;
  }

  h1 {
    color: #fbeb03;
    font-size: 18px;
    font-weight: 900;
    line-height: 22px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  p {
    margin-top: 1px;
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
    line-height: 16px;
  }
}

.gift-image {
  position: absolute;
  z-index: 1;
  top: -19px;
  right: 12px;
  width: 153px;
  height: 146px;
  object-fit: contain;
}

.account-card {
  position: relative;
  z-index: 3;
  height: 72px;
  margin-top: -49px;
  padding: 12px;
  box-sizing: border-box;
  border-radius: 16px;
  background: #ffffff;
  transition: height 0.2s ease;

  &.has-user {
    height: 112px;
  }
}

.input-wrap {
  position: relative;
  height: 48px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: #f1f1f1;
  box-sizing: border-box;

  &.active {
    border-color: #ff41ff;
  }
}

.user-input {
  width: 100%;
  height: 100%;
  padding: 0 42px 0 13px;
  box-sizing: border-box;
  border: 0;
  outline: 0;
  color: #333333;
  background: transparent;
  font: 700 14px/48px 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Poppins', sans-serif;

  &::placeholder {
    color: #c0c0c0;
    font-weight: 400;
    opacity: 1;
  }
}

.clear-button {
  position: absolute;
  top: 50%;
  right: 13px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #c8c8c8;
  transform: translateY(-50%);
  cursor: pointer;

  span::before,
  span::after {
    position: absolute;
    top: 8px;
    left: 4px;
    width: 10px;
    height: 2px;
    border-radius: 1px;
    background: #ffffff;
    content: '';
  }

  span::before {
    transform: rotate(45deg);
  }

  span::after {
    transform: rotate(-45deg);
  }
}

.user-info {
  height: 32px;
  margin: 8px 13px 0;
  display: flex;
  align-items: center;
  min-width: 0;
}

.user-avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.user-name {
  min-width: 0;
  margin-left: 10px;
  overflow: hidden;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.first-top-up {
  flex: 0 0 auto;
  height: 19px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 4px;
  color: #ffffff;
  background: linear-gradient(90deg, #fc1192, #fe8f19);
  font-size: 11px;
  font-weight: 500;
  line-height: 19px;
  white-space: nowrap;
}

.amount-section {
  margin-top: 24px;

  h2 {
    margin: 0 0 12px;
    color: #333333;
    font-size: 16px;
    font-weight: 800;
    line-height: 19px;
  }
}

.coin-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 10px;
}

.coin-option {
  min-width: 0;
  height: 84px;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 20px;
  color: #333333;
  background: #ffffff;
  box-sizing: border-box;
  cursor: pointer;

  &.active {
    background:
      linear-gradient(110deg, #f9e8ff, #e9e6ff) padding-box,
      linear-gradient(110deg, #ff41ff, #6922ff) border-box;

    .coin-amount strong {
      background: linear-gradient(90deg, #ff41ff, #6922ff);
      background-clip: text;
      color: transparent;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
}

.coin-skeleton {
  width: 100%;
  height: 84px;
  border-radius: 20px;
}

.coin-amount {
  max-width: calc(100% - 12px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;

  strong {
    font-family: 'DIN Alternate', 'Poppins', sans-serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 26px;
  }

  &.compact strong {
    font-size: 18px;
  }
}

.coin-icon {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
}

.coin-label {
  color: #666666;
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
}

.payment-section {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.payment-button {
  position: relative;
  width: 100%;
  height: 48px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 30px;
  color: #111111;
  background:
    linear-gradient(90deg, rgba(72, 67, 254, 0.12) 0%, rgba(255, 75, 167, 0.12) 100%) padding-box,
    linear-gradient(#FFFFFF, #FFFFFF) padding-box,
    linear-gradient(90deg, #4843FE 0%, #FF4BA7 100%) border-box;
  box-sizing: border-box;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
}

.payment-skeleton {
  width: 100%;
  height: 48px;
  border-radius: 30px;
}

.skeleton-shimmer {
  display: block;
  background: linear-gradient(100deg, #F1F1F1 25%, #FFFFFF 42%, #F1F1F1 59%);
  background-size: 220% 100%;
  animation: skeleton-shimmer 1.2s ease-in-out infinite;
}

.payermax-payment {
  color: #111111;
}

.discount-flag {
  position: absolute;
  z-index: 2;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;

  &.small {
    top: -8px;
    width: 83px;
    height: 18px;
    color: #B53CE2;
    background: url('@/img/flag_paypal.webp') center / 100% 100% no-repeat;
  }

  &.large {
    top: -20px;
    width: 97px;
    height: 32px;
    padding: 0;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    color: #ffffff;
    background: url('@/img/flag_bank_card.webp') center / 100% 100% no-repeat;

    span {
      margin-top: 13px;
      margin-left: 28px;
    }
  }
}

.payment-content {
  width: min(100%, 235px);
  height: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 32px minmax(62px, 1fr) max-content max-content;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;

  strong,
  b,
  del {
    white-space: nowrap;
  }

  strong,
  b {
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    line-height: 100%;
    letter-spacing: 0;
    color: #111111;
  }

  strong {
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
  }

  del {
    color: rgba(51, 51, 51, 0.3);
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    line-height: 100%;
    letter-spacing: 0;
    text-decoration: line-through;
  }
}

.payment-icon-wrap {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f4f4f4;
}

.payment-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.loading-spinner,
.page-loader {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(105, 34, 255, 0.25);
  border-top-color: #6922ff;
  border-radius: 50%;
  box-sizing: border-box;
  animation: spin 0.8s linear infinite;
}

.loading-overlay {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #ffffff;
  background: rgba(17, 17, 17, 0.68);
  font-size: 14px;
}

.page-loader {
  width: 34px;
  height: 34px;
  border-width: 3px;
  border-color: rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes skeleton-shimmer {
  to {
    background-position-x: -220%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-shimmer {
    animation: none;
  }
}

@media (min-width: 481px) {
  .yay-recharge-page {
    padding-right: 24px;
    padding-left: 24px;
  }

  .gift-copy {
    width: 250px;
  }

  .coin-options {
    grid-template-columns: repeat(3, minmax(108px, 1fr));
  }
}

@media (max-width: 359px) {
  .yay-recharge-page {
    padding-right: 12px;
    padding-left: 12px;
  }

  .gift-copy {
    padding-left: 12px;

    h1 {
      font-size: 16px;
    }

    p {
      font-size: 12px;
    }
  }

  .gift-image {
    right: -18px;
  }

  .coin-options {
    column-gap: 7px;
  }

  .coin-amount strong {
    font-size: 19px;
  }

  .coin-amount.compact strong {
    font-size: 16px;
  }

  .payment-content {
    width: calc(100% - 30px);
    grid-template-columns: 28px minmax(56px, 1fr) max-content max-content;
    gap: 6px;
  }

  .payment-icon-wrap {
    width: 28px;
    height: 28px;
  }

  .payment-content del {
    font-size: 12px;
  }
}
</style>
